var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "theme-ui/jsx-runtime";
import React from 'react';
import { COLOR } from '../../../theme/colors';
import { TYPOGRAPHY_TYPE, getIconSize, getTypographyStyles, } from '../../../theme/typography';
import { Icon } from '../Icon';
import { useEnvironment } from '../../providers/env';
import { SPINNER_SIZE, Spinner } from '../Spinner';
import { BOX_SHADOW } from '../../../theme/custom';
export var BUTTON_SIZE;
(function (BUTTON_SIZE) {
    BUTTON_SIZE["SM"] = "sm";
    BUTTON_SIZE["MD"] = "md";
    BUTTON_SIZE["LG"] = "lg";
})(BUTTON_SIZE || (BUTTON_SIZE = {}));
const typographyMap = {
    [BUTTON_SIZE.SM]: TYPOGRAPHY_TYPE.CONTROL_SMALL,
    [BUTTON_SIZE.MD]: TYPOGRAPHY_TYPE.CONTROL_MEDIUM,
    [BUTTON_SIZE.LG]: TYPOGRAPHY_TYPE.CONTROL_LARGE,
};
const defaultPaddingX = {
    [BUTTON_SIZE.SM]: 2,
    [BUTTON_SIZE.MD]: 3,
    [BUTTON_SIZE.LG]: 4,
};
const defaultHeight = {
    [BUTTON_SIZE.SM]: '2rem',
    [BUTTON_SIZE.MD]: '2.5rem',
    [BUTTON_SIZE.LG]: '3rem',
};
const iconMargin = {
    [BUTTON_SIZE.SM]: 1,
    [BUTTON_SIZE.MD]: 2,
    [BUTTON_SIZE.LG]: 2,
};
export var BUTTON_TYPE;
(function (BUTTON_TYPE) {
    BUTTON_TYPE["DEFAULT"] = "default";
    BUTTON_TYPE["TEXT"] = "text";
    BUTTON_TYPE["OUTLINE"] = "outline";
})(BUTTON_TYPE || (BUTTON_TYPE = {}));
export const Button = React.forwardRef((_a, ref) => {
    var { size: sizeParam, type: typeParam, color, customColor, textColor: textColorParam, primaryIcon, leftIcon, rightIcon, disabled: disabledParam, children, sx, loading, disableFocusStyles } = _a, rest = __rest(_a, ["size", "type", "color", "customColor", "textColor", "primaryIcon", "leftIcon", "rightIcon", "disabled", "children", "sx", "loading", "disableFocusStyles"]);
    const disabled = disabledParam || loading;
    const { flagInDevelopment } = useEnvironment();
    let bg = COLOR.PRIMARY;
    let borderColor = COLOR.TRANSPARENT;
    let cursor = 'pointer';
    let textColor = COLOR.BACKGROUND;
    let hoverShadow = BOX_SHADOW.NORMAL;
    let hoverDecoration = undefined;
    let hoverTransform = 'scale(1.03)';
    const isTextButton = typeParam === BUTTON_TYPE.TEXT;
    const isIconButton = primaryIcon;
    const type = typeParam || BUTTON_TYPE.DEFAULT;
    const size = sizeParam || BUTTON_SIZE.MD;
    const paddingX = isIconButton || isTextButton ? 0 : defaultPaddingX[size];
    const height = isTextButton ? undefined : defaultHeight[size];
    const width = primaryIcon ? height : undefined;
    if (isIconButton && !rest['aria-label']) {
        flagInDevelopment('Icon buttons should include aria-label prop for accessibility');
    }
    if (isIconButton) {
        hoverTransform = 'scale(1.05)';
    }
    if (type === BUTTON_TYPE.DEFAULT) {
        bg = color || COLOR.SECONDARY;
        textColor = COLOR.BLACK;
    }
    if (type === BUTTON_TYPE.TEXT) {
        bg = COLOR.TRANSPARENT;
        textColor = color || COLOR.PRIMARY;
        hoverShadow = undefined;
        hoverDecoration = 'underline';
    }
    if (type === BUTTON_TYPE.OUTLINE) {
        bg = COLOR.BACKGROUND;
        textColor = color || COLOR.TEXT;
        borderColor = textColor;
    }
    if (textColorParam) {
        textColor = textColorParam;
    }
    if (disabled) {
        cursor = 'not-allowed';
        if (bg === COLOR.BACKGROUND || bg === COLOR.TRANSPARENT) {
            textColor = COLOR.GRAY;
        }
        else {
            bg = COLOR.GRAY;
        }
    }
    const typography = typographyMap[size];
    const typographyStyles = getTypographyStyles(typography);
    let body = children;
    if (primaryIcon) {
        body = (_jsx(Icon, { icon: primaryIcon, size: getIconSize(typographyStyles.fontSize), color: textColor }));
    }
    if (loading) {
        body = (_jsxs("div", Object.assign({ sx: {
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            } }, { children: [_jsx(Spinner, { size: SPINNER_SIZE.SM, color: textColor, sx: { position: 'absolute' } }), _jsx("div", Object.assign({ sx: { visibility: 'hidden' } }, { children: body }))] })));
    }
    const hoverStyles = !disabled
        ? {
            boxShadow: hoverShadow,
            transform: hoverTransform,
            textDecoration: hoverDecoration,
        }
        : undefined;
    return (_jsxs("button", Object.assign({ ref: ref, tabIndex: disabled ? -1 : 0, disabled: disabled, css: {
            backgroundColor: customColor,
        }, sx: Object.assign(Object.assign({ cursor, bg: customColor ? undefined : bg, color: textColor, px: paddingX, width: width, height: height, border: 'solid 1px', borderRadius: '4px', borderColor, display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', ':hover': !disableFocusStyles ? hoverStyles : {}, ':focus': !disableFocusStyles ? hoverStyles : {} }, typographyStyles), sx) }, rest, { children: [leftIcon && (_jsx(Icon, { icon: leftIcon, size: typographyStyles.fontSize, sx: { mr: iconMargin[size] }, color: textColor })), _jsx("div", Object.assign({ css: {
                    flexGrow: 1,
                    justifyContent: 'flex-start',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textAlign: 'start',
                } }, { children: body })), rightIcon && (_jsx(Icon, { icon: rightIcon, size: typographyStyles.fontSize, sx: { ml: iconMargin[size] }, color: textColor }))] })));
});
export const LinkButton = (props) => {
    return (_jsx(Button, Object.assign({}, props, { color: COLOR.ND_BLUE_BRIGHT, textColor: COLOR.WHITE })));
};
